import { useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react'
import {
  ArrowPathIcon, ArrowsPointingOutIcon, ChatBubbleLeftRightIcon, PaperAirplaneIcon, SparklesIcon, TrashIcon, XMarkIcon,
} from '@heroicons/react/24/outline'
import { healthCyclesAPI } from '../../services/api'
import ChangeSetPreview from './ChangeSetPreview'
import ChatLookups from './ChatLookups'
import MemoryProposalCard from './MemoryProposalCard'
import ChangeSetNote from './ChangeSetNote'
import ChatMessageText from './ChatMessageText'
import ClearChatDialog from './ClearChatDialog'
import useStickToBottom from './useStickToBottom'
import { errorCode } from './cycleStatus'

const EXPANDED_KEY = 'vitahub.cycleChat.expanded'
const MAX_INPUT_PX = 240

const readExpanded = () => {
  try {
    return sessionStorage.getItem(EXPANDED_KEY) === '1'
  } catch {
    return false
  }
}
const writeExpanded = (value) => {
  try {
    sessionStorage.setItem(EXPANDED_KEY, value ? '1' : '0')
  } catch {
    // storage unavailable: the choice just isn't remembered
  }
}

// Each row renders once, even if the reply merged into the cache and a refetch both carry it.
const uniqueById = (rows) => [...new Map((rows ?? []).map((row) => [row.id, row])).values()]
const byCreated = (a, b) => String(a.created_at).localeCompare(String(b.created_at)) || a.id - b.id

// VH-227: one chat per cycle. Corrections come back as a proposed change set
// that is only applied after the user confirms it against the current version.
export function CycleChatPanel({ cycleId, readOnly = false, cycleRunning = false }) {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState('')
  // The message being sent (shown before the reply arrives); `error` set when it failed.
  const [pending, setPending] = useState(null)
  const [changeSetErrors, setChangeSetErrors] = useState({})
  const [expanded, setExpandedState] = useState(readExpanded)
  const [clearOpen, setClearOpen] = useState(false)
  const [clearError, setClearError] = useState(null)
  // VH-236: what the chat read for a turn, by assistant message id. Not stored server-side — a
  // fetched page in particular is a preview the user reviews, so it lives only in this session.
  const [lookups, setLookups] = useState({})
  // VH-235: memories the chat offered to keep, per assistant message. Not stored until saved.
  const [memoryProposals, setMemoryProposals] = useState({})
  const inputRef = useRef(null)

  const messagesKey = ['healthCycleMessages', cycleId]
  const { data, isLoading } = useQuery({
    queryKey: messagesKey,
    queryFn: () => healthCyclesAPI.listMessages(cycleId),
    select: (response) => response.data,
    enabled: !!cycleId,
  })

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: messagesKey })
    queryClient.invalidateQueries({ queryKey: ['healthCycle', cycleId] })
    queryClient.invalidateQueries({ queryKey: ['healthCycleHistory', cycleId] })
  }

  const errorText = (err) => {
    const code = errorCode(err)
    return t(`healthCycle.errors.${code}`, t('healthCycle.errors.generic'))
  }

  const sendMutation = useMutation({
    mutationFn: (content) => healthCyclesAPI.sendMessage(cycleId, content, i18n.language),
    onSuccess: (response) => {
      const turn = response?.data
      if (turn?.user_message && turn?.assistant_message) {
        // Show the reply at once; the refetch below brings superseded proposals up to date.
        queryClient.setQueryData(messagesKey, (old) => (old?.data ? {
          ...old,
          data: {
            ...old.data,
            messages: [...old.data.messages, turn.user_message, turn.assistant_message],
            change_sets: turn.change_set ? [...old.data.change_sets, turn.change_set] : old.data.change_sets,
            user_turns: turn.user_turns ?? old.data.user_turns,
          },
        } : old))
      }
      if (turn?.assistant_message && turn.lookups?.length) {
        setLookups((previous) => ({ ...previous, [turn.assistant_message.id]: turn.lookups }))
      }
      if (turn?.assistant_message && turn.memory_proposals?.length) {
        setMemoryProposals((previous) => ({ ...previous, [turn.assistant_message.id]: turn.memory_proposals }))
      }
      setPending(null)
      refresh()
    },
    onError: (err, content) => {
      setPending({ content, error: errorText(err) })
      setDraft((current) => (current.trim() ? current : content))
    },
  })

  const confirmMutation = useMutation({
    mutationFn: ({ changeSet, ids }) => healthCyclesAPI.confirmChangeSet(cycleId, changeSet.id, ids),
    onSuccess: (_res, { changeSet }) => {
      setChangeSetErrors((prev) => ({ ...prev, [changeSet.id]: null }))
      refresh()
    },
    onError: (err, { changeSet }) => {
      setChangeSetErrors((prev) => ({ ...prev, [changeSet.id]: errorText(err) }))
      // 409 superseded: the proposal is stale — reload so the recomputed state shows.
      if (err?.response?.status === 409) refresh()
    },
  })

  const rejectMutation = useMutation({
    mutationFn: (changeSet) => healthCyclesAPI.rejectChangeSet(cycleId, changeSet.id),
    onSuccess: refresh,
    onError: (err, changeSet) => setChangeSetErrors((prev) => ({ ...prev, [changeSet.id]: errorText(err) })),
  })

  const clearMutation = useMutation({
    mutationFn: () => healthCyclesAPI.clearMessages(cycleId),
    onSuccess: () => {
      setClearOpen(false)
      setClearError(null)
      setPending(null)
      refresh()
    },
    onError: (err) => setClearError(errorText(err)),
  })

  const messages = uniqueById(data?.messages).sort(byCreated)
  const changeSets = uniqueById(data?.change_sets)
  const byMessage = changeSets.reduce((acc, cs) => {
    if (cs.message_id != null) (acc[cs.message_id] ||= []).push(cs)
    return acc
  }, {})
  // Without a message only an open proposal still needs the chat; decided ones live in the change history.
  const orphanSets = changeSets.filter((cs) => cs.message_id == null && cs.status === 'proposed')
  const userTurns = data?.user_turns ?? 0
  const turnCap = data?.turn_cap ?? 50
  const atCap = userTurns >= turnCap
  const sending = sendMutation.isPending
  const canSend = !readOnly && !cycleRunning && !atCap && !sending && draft.trim().length > 0
  const busy = confirmMutation.isPending || rejectMutation.isPending

  const scrollTrigger = [
    isLoading, expanded, messages.length, pending?.content, pending?.error,
    changeSets.map((cs) => `${cs.id}:${cs.status}`).join(','),
  ].join('|')
  const scroll = useStickToBottom(scrollTrigger)

  // Auto-grow the composer up to a limit, then scroll inside it.
  useLayoutEffect(() => {
    const el = inputRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, MAX_INPUT_PX)}px`
  }, [draft, expanded])

  const send = (content) => {
    setPending({ content, error: null })
    scroll.stick()
    sendMutation.mutate(content)
  }

  const submit = (e) => {
    e?.preventDefault()
    if (!canSend) return
    const content = draft.trim()
    setDraft('')
    send(content)
  }

  const retry = () => {
    if (!pending || sending) return
    const content = pending.content
    setDraft((current) => (current.trim() === content ? '' : current))
    send(content)
  }

  const setExpanded = (value) => {
    setExpandedState(value)
    writeExpanded(value)
  }

  // Only an open proposal renders as the full box; decided ones collapse to a one-line note.
  const renderChangeSet = (cs) => (cs.status !== 'proposed' ? <ChangeSetNote key={cs.id} changeSet={cs} /> : (
    <ChangeSetPreview
      key={cs.id}
      changeSet={cs}
      busy={busy}
      readOnly={readOnly}
      error={changeSetErrors[cs.id]}
      onConfirm={(changeSet, ids) => confirmMutation.mutate({ changeSet, ids })}
      onReject={(changeSet) => rejectMutation.mutate(changeSet)}
    />
  ))

  const bubble = (role, content, large) => (
    <div
      className={`max-w-[85%] rounded-lg px-3 py-2 ${large ? 'text-base leading-relaxed' : 'text-sm'} ${
        role === 'user' ? 'bg-purple-600 text-on-neutral whitespace-pre-wrap' : 'bg-gray-100 text-gray-800 break-words'
      }`}
    >
      {role === 'user' ? content : <ChatMessageText content={content} />}
    </div>
  )

  const header = (large) => (
    <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-2">
      {large ? (
        <DialogTitle className="font-medium text-gray-900 flex items-center">
          <ChatBubbleLeftRightIcon className="h-5 w-5 text-purple-600 mr-2" />
          {t('healthCycle.chat.title')}
        </DialogTitle>
      ) : (
        <h3 className="font-medium text-gray-900 flex items-center">
          <ChatBubbleLeftRightIcon className="h-5 w-5 text-purple-600 mr-2" />
          {t('healthCycle.chat.title')}
        </h3>
      )}
      <div className="flex items-center gap-1">
        <span className={`text-xs mr-1 ${atCap ? 'text-red-600' : 'text-gray-500'}`}>
          {t('healthCycle.chat.turns', { used: userTurns, cap: turnCap })}
        </span>
        {!readOnly && messages.length > 0 && (
          <button
            type="button"
            className="p-1.5 rounded text-gray-500 hover:text-red-600 hover:bg-gray-100"
            onClick={() => { setClearError(null); setClearOpen(true) }}
            aria-label={t('healthCycle.chat.clear')}
            title={t('healthCycle.chat.clear')}
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        )}
        {large ? (
          <button
            type="button"
            className="p-1.5 rounded text-gray-500 hover:text-gray-900 hover:bg-gray-100"
            onClick={() => setExpanded(false)}
            aria-label={t('healthCycle.chat.close')}
            title={t('healthCycle.chat.close')}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        ) : (
          <button
            type="button"
            className="p-1.5 rounded text-gray-500 hover:text-gray-900 hover:bg-gray-100"
            onClick={() => setExpanded(true)}
            aria-label={t('healthCycle.chat.expand')}
            title={t('healthCycle.chat.expand')}
          >
            <ArrowsPointingOutIcon className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  )

  const body = (large) => {
    const column = large ? 'max-w-3xl mx-auto w-full' : ''
    return (
      <>
        {header(large)}
        <p className={`px-4 pt-2 text-xs text-gray-600 ${column}`}>{t('healthCycle.chat.subtitle')}</p>

        <div
          ref={scroll.ref}
          onScroll={scroll.onScroll}
          className={`p-4 overflow-y-auto ${large ? 'flex-1 min-h-0' : 'max-h-[520px] min-h-[160px]'}`}
          data-testid="chat-messages"
        >
          <div className={`space-y-3 ${column}`}>
            {isLoading ? (
              <p className="text-sm text-gray-400 text-center">{t('common.loading')}</p>
            ) : messages.length === 0 && !pending ? (
              <p className="text-sm text-gray-400 text-center py-6">{t('healthCycle.chat.empty')}</p>
            ) : (
              messages.map((msg) => (
                <div key={msg.id} className="space-y-2">
                  <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    {bubble(msg.role, msg.content, large)}
                  </div>
                  {msg.role === 'assistant' && <ChatLookups lookups={lookups[msg.id]} />}
                  {msg.role === 'assistant' && (memoryProposals[msg.id] ?? []).map((p) => (
                    <MemoryProposalCard key={`${msg.id}-${p.text}`} proposal={p} />
                  ))}
                  {(byMessage[msg.id] ?? []).map(renderChangeSet)}
                </div>
              ))
            )}
            {orphanSets.map(renderChangeSet)}
            {pending && (
              <div className="space-y-1" data-testid="chat-pending">
                <div className={`flex justify-end ${pending.error ? 'opacity-60' : ''}`}>
                  {bubble('user', pending.content, large)}
                </div>
                {pending.error ? (
                  <div className="flex items-center justify-end gap-2 text-xs text-red-700" role="alert">
                    <span>{pending.error}</span>
                    <button
                      type="button"
                      className="inline-flex items-center whitespace-nowrap font-medium text-purple-700 hover:text-purple-900"
                      onClick={retry}
                      disabled={sending}
                    >
                      <ArrowPathIcon className="h-3.5 w-3.5 mr-1" />
                      {t('healthCycle.chat.retry')}
                    </button>
                  </div>
                ) : (
                  <p className="flex items-center text-sm text-gray-500">
                    <SparklesIcon className="h-4 w-4 mr-2 text-purple-500 animate-pulse" />
                    {t('healthCycle.chat.thinking')}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {readOnly ? (
          <p className="border-t border-gray-200 p-3 text-xs text-gray-500">{t('healthCycle.chat.readOnly')}</p>
        ) : cycleRunning ? (
          // VH-233: no chat while the cycle recomputes
          <p className="border-t border-gray-200 p-3 text-xs text-gray-500" data-testid="chat-busy">{t('healthCycle.errors.cycle_running')}</p>
        ) : atCap ? (
          <p className="border-t border-gray-200 p-3 text-sm text-gray-700">{t('healthCycle.chat.capReached', { cap: turnCap })}</p>
        ) : (
          <form onSubmit={submit} className="border-t border-gray-200 p-3">
            <div className={`flex items-end gap-2 ${column}`}>
              <textarea
                ref={inputRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) submit(e)
                }}
                rows={large ? 3 : 2}
                maxLength={4000}
                placeholder={t('healthCycle.chat.placeholder')}
                aria-label={t('healthCycle.chat.placeholder')}
                className={`flex-1 resize-none px-3 py-2 overflow-y-auto ${large ? 'text-base' : 'text-sm'}`}
              />
              <button type="submit" className="btn-primary p-2" disabled={!canSend} aria-label={t('healthCycle.chat.send')}>
                <PaperAirplaneIcon className="h-5 w-5" />
              </button>
            </div>
          </form>
        )}
      </>
    )
  }

  return (
    <>
      {expanded ? (
        <section className="card p-4 flex items-center justify-between gap-2" aria-label={t('healthCycle.chat.title')}>
          <p className="text-sm text-gray-600 flex items-center">
            <ChatBubbleLeftRightIcon className="h-5 w-5 text-purple-600 mr-2" />
            {t('healthCycle.chat.openInLarge')}
          </p>
          <button type="button" className="btn-outline text-sm" onClick={() => setExpanded(true)}>
            {t('healthCycle.chat.show')}
          </button>
        </section>
      ) : (
        <section className="card flex flex-col" aria-label={t('healthCycle.chat.title')}>
          {body(false)}
        </section>
      )}

      <Dialog open={expanded} onClose={() => setExpanded(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-2 sm:p-6">
          <DialogPanel className="card w-full max-w-5xl h-[92vh] flex flex-col overflow-hidden shadow-xl">
            {body(true)}
          </DialogPanel>
        </div>
      </Dialog>

      <ClearChatDialog
        open={clearOpen}
        onClose={() => setClearOpen(false)}
        onConfirm={() => clearMutation.mutate()}
        busy={clearMutation.isPending}
        error={clearError}
      />
    </>
  )
}

export default CycleChatPanel
